import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Boxes, Copy, Loader2, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { EnvironmentBadge } from "@/components/EnvironmentBadge";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";

const ENV_TYPES = [
  { value: "development", label: "Development" },
  { value: "qa", label: "QA" },
  { value: "uat", label: "UAT" },
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
  { value: "custom", label: "Custom" },
];

const SAFETY_MODES = [
  { value: "allow_all", label: "Allow all tests" },
  { value: "smoke_only", label: "Smoke only" },
  { value: "read_only", label: "Read-only tests only" },
  { value: "require_confirmation", label: "Require confirmation" },
  { value: "block_destructive", label: "Block destructive tests" },
];

const BROWSERS = [
  { value: "chromium", label: "Chromium" },
  { value: "firefox", label: "Firefox" },
  { value: "webkit", label: "WebKit / Safari" },
];

function EnvDialog({ open, onOpenChange, onSaved, projectId, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    type: initial?.type || "custom",
    base_url: initial?.base_url || "",
    description: initial?.description || "",
    default_browser: initial?.default_browser || "chromium",
    safety_mode: initial?.safety_mode || "allow_all",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.base_url.trim()) {
      toast.error("Name and Base URL are required");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/environments/${initial.id}`, form);
        toast.success("Environment updated");
      } else {
        await api.post(`/projects/${projectId}/environments`, form);
        toast.success("Environment created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] rounded-xl border-[#E5E7EB]" data-testid="env-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">{isEdit ? "Edit environment" : "New environment"}</DialogTitle>
          <DialogDescription>Configure a target environment — Dev, QA, UAT, Staging or Production.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569]">Name</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="UAT" data-testid="env-name-input" className="rounded-md border-[#E5E7EB]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569]">Type</label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="env-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENV_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`env-type-option-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">Base URL</label>
            <Input value={form.base_url} onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))} placeholder="https://uat.app.example.com" data-testid="env-base-url-input" className="rounded-md border-[#E5E7EB] font-mono text-xs" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} data-testid="env-description-input" className="rounded-md border-[#E5E7EB]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569]">Default browser</label>
              <Select value={form.default_browser} onValueChange={(v) => setForm((f) => ({ ...f, default_browser: v }))}>
                <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="env-browser-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BROWSERS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569]">Safety mode</label>
              <Select value={form.safety_mode} onValueChange={(v) => setForm((f) => ({ ...f, safety_mode: v }))}>
                <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="env-safety-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SAFETY_MODES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleSave} disabled={submitting} data-testid="env-submit-button" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 flex items-center gap-1.5">
            {submitting && <Loader2 size={13} className="animate-spin" />}
            {isEdit ? "Save" : "Create environment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloneDialog({ open, onOpenChange, source, onCloned }) {
  const [form, setForm] = useState({ name: "", type: "staging", include_login_states: true });
  const handleClone = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post(`/environments/${source.id}/clone`, form);
      toast.success("Environment cloned");
      onCloned?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-xl border-[#E5E7EB]" data-testid="env-clone-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">Clone environment</DialogTitle>
          <DialogDescription>Copies variables, secrets and login states from <span className="font-medium">{source?.name}</span>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">New name</label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Staging" data-testid="clone-name-input" className="rounded-md border-[#E5E7EB]" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="clone-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENV_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#475569]">
            <input type="checkbox" checked={form.include_login_states} onChange={(e) => setForm((f) => ({ ...f, include_login_states: e.target.checked }))} data-testid="clone-include-login-checkbox" />
            Also clone login states
          </label>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleClone} data-testid="clone-submit-button" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB]">Clone</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectEnvironmentsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [envs, setEnvs] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [cloning, setCloning] = useState(null);
  const [cloneOpen, setCloneOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/environments`)
      .then((r) => setEnvs(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId]);
  useEffect(load, [load]);

  const handleDelete = async (e) => {
    const ok = await confirm({
      title: `Delete "${e.name}"?`,
      description: "This is irreversible. Variables, secrets and any login states attached to this environment will be removed.",
      confirmLabel: "Delete environment",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/environments/${e.id}`);
      toast.success("Environment deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const columns = [
    {
      key: "env",
      label: "Environment",
      mobileVariant: "title",
      render: (e) => (
        <Link to={`/projects/${projectId}/environments/${e.id}`} data-testid={`env-link-${e.id}`} className="flex flex-col rt-primary hover:text-[#3B82F6]">
          <span>{e.name}</span>
          {e.description && <span className="text-xs text-[#64748B] mt-0.5 font-normal line-clamp-1">{e.description}</span>}
        </Link>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: (e) => <EnvironmentBadge type={e.type} testId={`env-type-badge-${e.id}`} />,
    },
    {
      key: "base_url",
      label: "Base URL",
      render: (e) => <span className="rt-value text-xs text-[#64748B] font-mono truncate">{e.base_url?.replace(/^https?:\/\//, "") || "—"}</span>,
    },
    { key: "logins", label: "Login states", render: (e) => <span className="text-[#334155]">{e.login_state_count}</span> },
    { key: "tests", label: "Tests", render: (e) => <span className="text-[#334155]">{e.test_count}</span> },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (e) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => navigate(`/projects/${projectId}/environments/${e.id}`)}
            className="inline-flex items-center px-3 h-8 rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC]"
            data-testid={`env-open-${e.id}`}
          >
            Open
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`env-row-actions-${e.id}`} aria-label="Row actions" className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]">
                <MoreHorizontal size={14} className="text-[#64748B]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg border-[#E5E7EB] p-1 w-44">
              <DropdownMenuItem onClick={() => { setEditing(e); setEditOpen(true); }} className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]" data-testid={`env-edit-${e.id}`}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setCloning(e); setCloneOpen(true); }} className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]" data-testid={`env-clone-${e.id}`}>
                <Copy size={13} className="mr-2" /> Clone…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDelete(e)} className="rounded-md px-2 py-2 text-sm cursor-pointer text-[#B91C1C] focus:bg-[#FEF2F2]" data-testid={`env-delete-${e.id}`}>
                <Trash2 size={13} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="project-environments-page">
      <PageHeader
        title="Environments"
        description="Manage Development, QA, UAT, Staging and Production environments. Each environment owns its own base URL, variables, secrets and login states."
        actions={
          <button onClick={() => setCreateOpen(true)} data-testid="new-environment-button" className="inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors">
            <Plus size={15} />
            New environment
          </button>
        }
      />

      {envs === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : envs.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-environments-message">
          <Boxes size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No environments yet</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">Create your first environment to start running tests against a target host.</p>
        </div>
      ) : (
        <ResponsiveTable
          testId="environments-table"
          columns={columns}
          rows={envs}
          getRowTestId={(e) => `env-row-${e.id}`}
        />
      )}

      <EnvDialog key={`new-${createOpen}`} open={createOpen} onOpenChange={setCreateOpen} onSaved={load} projectId={projectId} />
      <EnvDialog key={`edit-${editing?.id || "none"}-${editOpen}`} open={editOpen} onOpenChange={setEditOpen} onSaved={load} projectId={projectId} initial={editing} />
      <CloneDialog key={`clone-${cloning?.id || "none"}-${cloneOpen}`} open={cloneOpen} onOpenChange={setCloneOpen} source={cloning} onCloned={load} />
    </div>
  );
}
